import { virtualize } from "@lit-labs/virtualizer/virtualize.js";
import {
    type DateRange,
    type EmployeeSortProperty,
    GetBalancesParams,
    type TimeBalance,
    employeeNameOrderForSort,
    formatDate,
    formatRange,
    getEmployeeName,
    getRange,
    isAbortError,
    serializeColumOptions,
    sumTimeBalances,
    toDurationString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { popover } from "../../../directives/popover";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/date-range-picker";
import "../../../elements/popover";
import { app } from "../../../init";
import { LatestRequest } from "../../../lib/latest-request";
import { singleton } from "../../../lib/singleton";
import { Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { VirtualizerMixin } from "../../../mixins/virtualizer";
import { shared } from "../../../styles";
import "../components/recalcuate-results-dialog";
import type { RecalculateResultsDialog } from "../components/recalcuate-results-dialog";
import "../components/reset-balances-dialog";
import type { ResetBalancesDialog } from "../components/reset-balances-dialog";
import "../employees-filter";

@customElement("ptc-employees-time-balance-all")
export class EmployeesTimeBalanceAll extends VirtualizerMixin(Routing(StateMixin(LitElement))) {
    routePattern = /^employees\/all\/time-balance/;

    get routeTitle() {
        return "Arbeitszeitkonto: Alle Mitarbeiter";
    }

    @state()
    private _balances: TimeBalance[] = [];

    @state()
    private _loading = false;

    private _request = new LatestRequest(this);

    @singleton("ptc-reset-balances-dialog")
    private _resetBalancesDialog: ResetBalancesDialog;

    @singleton("ptc-recalculate-results-dialog")
    private _recalculateResultsDialog: RecalculateResultsDialog;

    defaultRange = () => getRange(new Date(), "month");

    rangeChanged = () => this.load();

    async load() {
        if (!this.dateRange) {
            return;
        }

        this._loading = true;

        try {
            this._balances = await app.api.getTimeBalances(
                new GetBalancesParams({
                    from: this.dateRange.from,
                    to: this.dateRange.to,
                    resolution: "none",
                }),
                { signal: this._request.start() }
            );
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _resetBalances() {
        const didChange = await this._resetBalancesDialog.show({
            date: this.dateFrom,
            filters: app.employeeFilters.filters,
        });
        if (didChange) {
            return this.load();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                --color-cell: var(--color-bg-translucent);
                display: flex;
                flex-direction: column;
            }

            .scroller {
                flex: 1;
                overflow: auto;
            }

            table {
                border-collapse: separate;
                display: block;
                width: fit-content;
            }

            thead {
                display: block;
                position: sticky;
                top: 0;
                z-index: 2;
                background:;
            }

            tbody {
                display: block;
                box-sizing: border-box;
            }

            thead th,
            tbody th:first-child {
                background: var(--color-cell);
            }

            thead th:first-child,
            tbody th:first-child {
                position: sticky;
                left: 0;
                z-index: 1;
            }

            tr {
                display: grid;
                grid-template-columns: var(--column-width-employee) 16ch repeat(4, 13ch);
                align-items: center;
                width: 100%;
                width: min-content;
            }

            tbody > tr:not(:last-of-type) {
                border-bottom: solid 1px var(--shade-2);
            }

            th,
            td {
                padding: 0.5em;
                vertical-align: middle;
                height: 100%;
                box-sizing: border-box;
            }

            td {
                text-align: right;
            }

            thead {
                font-weight: bold;
                text-align: center;
            }

            tbody.total {
                font-weight: bold;
                box-shadow: rgb(0 0 0 / 10%) 0 1px 0;
                position: sticky;
                top: 2.3em;
                z-index: 2;
            }
            tbody.total td {
                background: var(--color-cell);
            }
        `,
    ];

    private _renderTimeBalances() {
        const filteredEmployees = app.getFilteredEmployees(this.dateRange ?? undefined);
        const total = sumTimeBalances(
            this._balances.filter((b) => filteredEmployees.some((e) => e.id === b.employeeId))
        );

        return html`
            <table class="fill-horizontally">
                <thead>
                    <tr>
                        <th></th>
                        <th scope="col">Übertrag</th>
                        <th scope="col">Soll</th>
                        <th scope="col">Ist</th>
                        <th scope="col">Differenz</th>
                        <th scope="col">Saldo</th>
                    </tr>
                </thead>

                <tbody class="total">
                    <tr>
                        <th scope="row"><ptc-ui-icon name="icon.feature.employees"></ptc-ui-icon> Gesamt</th>
                        <td>
                            <ptc-ui-balance
                                .value=${total.reset ?? total.carry}
                                .parser=${(val: number) => toDurationString(val, true)}
                            ></ptc-ui-balance>
                        </td>
                        <td>${toDurationString(total.nominal)}</td>
                        <td>${toDurationString(total.actual)}</td>
                        <td>
                            <ptc-ui-balance
                                .value=${total.difference}
                                .parser=${(val: number) => toDurationString(val, true)}
                                iconStyle="up-down"
                                class="semibold"
                            ></ptc-ui-balance>
                        </td>
                        <td>
                            <ptc-ui-balance
                                class="bold"
                                .value=${total.balance}
                                .parser=${(val: number) => toDurationString(val, true)}
                            ></ptc-ui-balance>
                        </td>
                    </tr>
                </tbody>

                <tbody data-virtualized>
                    ${virtualize({
                        items: filteredEmployees,
                        renderItem: (employee) => {
                            const balance = this._balances.find((s) => s.employeeId === employee.id);

                            if (!balance) {
                                return html``;
                            }

                            return html`
                                <tr class="click" @click=${() => this.go(`employees/${employee.id}/time-balance`)}>
                                    <th>
                                        <div class="horizontal center-aligning spacing layout employee-header">
                                            <ptc-ui-avatar
                                                class="tinier"
                                                .employee=${employee}
                                                .positionColors=${employee.positions.map((p) =>
                                                    app.company.getPositionColor(p)
                                                )}
                                            ></ptc-ui-avatar>
                                            <div class="stretch ellispis collapse">
                                                <span class="semibold">
                                                    ${getEmployeeName(
                                                        employee,
                                                        employeeNameOrderForSort(app.employeeFilters.sortProperty)
                                                    )}
                                                </span>
                                                ${employee.staffNumber
                                                    ? html` <span class="subtle">#${employee.staffNumber}</span> `
                                                    : ""}
                                            </div>
                                        </div>
                                    </th>
                                    <td
                                        ${typeof balance.reset === "number"
                                            ? popover(html`Konto zurückgesetzt am ${formatDate(balance.from)}`, {
                                                  class: "tooltip",
                                                  trigger: "hover",
                                              })
                                            : nothing}
                                    >
                                        <ptc-ui-balance
                                            .value=${balance.carry}
                                            .parser=${(val: number) => toDurationString(val, true)}
                                        ></ptc-ui-balance>
                                        ${typeof balance.reset === "number"
                                            ? html`
                                                  <ptc-ui-icon
                                                      name="icon.navigation.forward"
                                                      class="smaller"
                                                  ></ptc-ui-icon>
                                                  <ptc-ui-balance
                                                      class="bold"
                                                      .value=${balance.reset}
                                                      .parser=${(val: number) => toDurationString(val, true)}
                                                  ></ptc-ui-balance>
                                              `
                                            : ""}
                                    </td>
                                    <td>${toDurationString(balance.nominal)}</td>
                                    <td>
                                        ${balance.adjustments || balance.absences
                                            ? html`<sup
                                                  class="tinier"
                                                  ${popover(
                                                      html`
                                                          <table class="simple">
                                                              <thead>
                                                                  <tr>
                                                                      <th style="text-align: left;">Ursache</th>
                                                                      <th>Stunden</th>
                                                                  </tr>
                                                              </thead>
                                                              <tbody>
                                                                  <tr>
                                                                      <th>
                                                                          <ptc-ui-icon
                                                                              name="icon.time-entry.work"
                                                                          ></ptc-ui-icon>
                                                                          Gearbeitet
                                                                      </th>
                                                                      <td>${toDurationString(balance.work)}</td>
                                                                  </tr>
                                                                  ${balance.absences
                                                                      ? html`
                                                                            <tr>
                                                                                <th>
                                                                                    <ptc-ui-icon
                                                                                        name="icon.object.absence"
                                                                                    ></ptc-ui-icon>
                                                                                    Urlaub / Krank
                                                                                </th>
                                                                                <td>
                                                                                    ${toDurationString(
                                                                                        balance.absences
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        `
                                                                      : ""}
                                                                  ${balance.adjustments
                                                                      ? html`
                                                                            <tr>
                                                                                <th>
                                                                                    <ptc-ui-icon
                                                                                        name="icon.object.booking"
                                                                                    ></ptc-ui-icon>
                                                                                    Zeitbuchungen
                                                                                </th>
                                                                                <td>
                                                                                    ${toDurationString(
                                                                                        balance.adjustments
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        `
                                                                      : ""}
                                                              </tbody>
                                                          </table>
                                                      `,
                                                      {
                                                          trigger: "hover",
                                                          class: "text-left-aligning tooltip non-interactive",
                                                      }
                                                  )}
                                              >
                                                  ${balance.absences
                                                      ? html`<ptc-ui-icon name="icon.object.absence"></ptc-ui-icon>`
                                                      : ""}${balance.adjustments
                                                      ? html`<ptc-ui-icon name="icon.object.booking"></ptc-ui-icon>`
                                                      : ""}
                                              </sup> `
                                            : ""}
                                        ${toDurationString(balance.actual)}
                                    </td>
                                    <td>
                                        <ptc-ui-balance
                                            .value=${balance.difference}
                                            iconStyle="up-down"
                                            class="semibold"
                                            .parser=${(val: number) => toDurationString(val, true)}
                                        ></ptc-ui-balance>
                                    </td>
                                    <td>
                                        ${balance.subBalances
                                            ? html`<sup
                                                  class="tinier bold"
                                                  ${popover(
                                                      html`
                                                          <table class="simple">
                                                              <thead>
                                                                  <tr>
                                                                      <th></th>
                                                                      <th scope="col">Übertrag</th>
                                                                      <th scope="col">Soll</th>
                                                                      <th scope="col">Ist</th>
                                                                      <th scope="col">Differenz</th>
                                                                      <th scope="col">Saldo</th>
                                                                  </tr>
                                                              </thead>

                                                              <tbody class="total">
                                                                  ${balance.subBalances.map(
                                                                      (subBalance) => html`
                                                                          <tr>
                                                                              <th>${formatRange(subBalance)}</th>
                                                                              <td
                                                                                  class="center-aligning end-justifying horizontal layout"
                                                                              >
                                                                                  <ptc-ui-balance
                                                                                      .value=${subBalance.carry}
                                                                                      .parser=${(val: number) =>
                                                                                          toDurationString(val, true)}
                                                                                  ></ptc-ui-balance>
                                                                                  ${typeof subBalance.reset === "number"
                                                                                      ? html`
                                                                                            <ptc-ui-icon
                                                                                                name="icon.navigation.forward"
                                                                                                class="smaller"
                                                                                            ></ptc-ui-icon>
                                                                                            <ptc-ui-balance
                                                                                                class="bold"
                                                                                                .value=${subBalance.reset}
                                                                                                .parser=${(
                                                                                                    val: number
                                                                                                ) =>
                                                                                                    toDurationString(
                                                                                                        val,
                                                                                                        true
                                                                                                    )}
                                                                                            ></ptc-ui-balance>
                                                                                        `
                                                                                      : ""}
                                                                              </td>
                                                                              <td>
                                                                                  ${toDurationString(
                                                                                      subBalance.nominal
                                                                                  )}
                                                                              </td>
                                                                              <td>
                                                                                  ${toDurationString(subBalance.actual)}
                                                                              </td>
                                                                              <td>
                                                                                  <ptc-ui-balance
                                                                                      .value=${subBalance.difference}
                                                                                      iconStyle="up-down"
                                                                                      class="semibold"
                                                                                      .parser=${(val: number) =>
                                                                                          toDurationString(val, true)}
                                                                                  ></ptc-ui-balance>
                                                                              </td>
                                                                              <td>
                                                                                  <ptc-ui-balance
                                                                                      class="bold"
                                                                                      .value=${subBalance.balance}
                                                                                      .parser=${(val: number) =>
                                                                                          toDurationString(val, true)}
                                                                                  ></ptc-ui-balance>
                                                                              </td>
                                                                          </tr>
                                                                      `
                                                                  )}
                                                              </tbody>
                                                          </table>
                                                      `,
                                                      {
                                                          trigger: "hover",
                                                          class: "non-interactive",
                                                      }
                                                  )}
                                              >
                                                  <ptc-ui-icon name="icon.format.bullet-list"></ptc-ui-icon>
                                              </sup> `
                                            : ""}
                                        <ptc-ui-balance
                                            class="bold"
                                            .value=${balance.balance}
                                            .parser=${(val: number) => toDurationString(val, true)}
                                        ></ptc-ui-balance>
                                    </td>
                                </tr>
                            `;
                        },
                    })}
                </tbody>
            </table>
        `;
    }

    render() {
        return html`
            <div class="center-aligning horizontal layout padded">
                <ptc-date-range-picker
                    @range-selected=${(e: CustomEvent<DateRange>) => this.go(null, e.detail)}
                    .range=${this.dateRange}
                    maxDays=${366}
                ></ptc-date-range-picker>

                <div class="stretch end-justifying horizontal layout collapse">
                    <button
                        class="slim transparent"
                        title="Arbeitszeiten als Excel-Datei Exportieren"
                        @click="${() => {
                            this.go("exports/employee-table-excel", {
                                from: this.dateRange?.from,
                                to: this.dateRange?.to,
                                "entity-filters": JSON.stringify(app.employeeFilters.filters),
                                columns: serializeColumOptions([
                                    { type: "fullId" },
                                    { type: "timeBalanceCarry" },
                                    { type: "timeBalanceNominal" },
                                    { type: "timeBalanceActual" },
                                    { type: "timeBalanceDifference" },
                                    { type: "timeBalanceBalance" },
                                ]),
                            });
                        }}"
                    >
                        <ptc-ui-icon name="icon.file.excel"></ptc-ui-icon>
                    </button>

                    ${app.hasPermission("manage.exports.ledgers_time")
                        ? html`<button
                              class="transparent slim right-margined"
                              title="Arbeitszeiten Exportieren"
                              @click=${() =>
                                  this.go("exports/balances-time-excel", {
                                      from: this.dateFrom,
                                      to: this.dateTo,
                                      "entity-filters": JSON.stringify(app.employeeFilters.filters),
                                  })}
                          >
                              <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                          </button>`
                        : nothing}

                    <button id="moreMenuButton" class="slim transparent" title="Mehr">
                        <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                    </button>

                    <ptc-ui-popover for="moreMenuButton" placement="bottom" class="popover-menu" data-popover="close">
                        <button @click=${this._resetBalances}>
                            <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon> Konten Zurücksetzen
                        </button>
                        <button
                            @click=${async () => {
                                const recalculated = await this._recalculateResultsDialog.show({
                                    filters: app.employeeFilters.filters,
                                });
                                if (recalculated) {
                                    void this.load();
                                }
                            }}
                        >
                            <ptc-ui-icon name="icon.action.recalculate"></ptc-ui-icon> Konten Neu Berechnen
                        </button>
                    </ptc-ui-popover>
                </div>
            </div>

            <ptc-employees-filter
                class="border-top border-bottom"
                .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
            ></ptc-employees-filter>

            <div class="scroller">
                <div class="double-padded">${this._renderTimeBalances()}</div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
